
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Text,
  Button,
  useToast,
} from '@chakra-ui/react'
import { useState, useEffect } from 'react'
import { userActivityService, ActiveSession } from '@/lib/services/userActivityService'
import { useThemeColors } from '@/hooks/useThemeColors'

interface ActiveSessionsProps {
  userId: number
}

export function ActiveSessions({ userId }: ActiveSessionsProps) {
  const colors = useThemeColors()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessions, setSessions] = useState<ActiveSession[]>([])
  const toast = useToast()

  useEffect(() => {
    loadSessions()
  }, [userId])

  const loadSessions = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await userActivityService.getActiveSessions(userId)
      setSessions(response.data)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load active sessions')
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeSession = async (tokenId: number) => {
    if (!confirm('Are you sure you want to revoke this session?')) {
      return
    }

    try {
      await userActivityService.revokeSession(userId, tokenId)
      toast({
        title: 'Session revoked',
        description: 'The session has been successfully revoked.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      loadSessions()
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to revoke session',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <Box textAlign="center" py={4}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={2}>Loading active sessions...</Text>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    )
  }

  if (sessions.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Text color={colors.text}>
          No active sessions found for this user
        </Text>
      </Box>
    )
  }

  return (
    <Box overflowX="auto">
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th>Session Name</Th>
            <Th>Created At</Th>
            <Th>Last Activity</Th>
            <Th>Expires At</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {sessions.map((session) => (
            <Tr key={session.id}>
              <Td>{session.name}</Td>
              <Td>{formatDateTime(session.created_at)}</Td>
              <Td>{formatDateTime(session.last_used_at)}</Td>
              <Td>{formatDateTime(session.expires_at)}</Td>
              <Td>
                <Badge colorScheme={session.is_active ? 'green' : 'gray'}>
                  {session.is_active ? 'Active' : 'Expired'}
                </Badge>
              </Td>
              <Td>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => handleRevokeSession(session.id)}
                  isDisabled={!session.is_active}
                >
                  Revoke
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}

